import React, { useMemo, useState } from 'react';
import { ChartCard, chartColors } from './ChartCard';
import { FilterChip } from './FilterChip';
import { SectionTitle } from './ui/Card';
import { metricsForSport } from '../data/sportMetrics';
import { useLocale } from '../contexts/LocaleContext';
import type { ChartRange, SportId } from '../types';
import { buildSeries, sliceRange } from '../utils/series';

/** Stable per-metric seed so each metric keeps its own demo history. */
function seedFor(id: string): number {
  return id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 97 + 3;
}

/**
 * Flexible sport-performance evolution. The options are read from the sport's metric
 * metadata (`chart: true` with a recorded value), so any sport or metric added to
 * data/sportMetrics.ts appears here without code changes.
 */
export function SportEvolutionChart({
  sport
}: {
  sport: SportId;
}) {
  const {
    t,
    tl
  } = useLocale();
  const options = useMemo(() => metricsForSport(sport).filter(metric => metric.chart && metric.value > 0), [sport]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [range, setRange] = useState<ChartRange>('1y');
  const selected = options.find(metric => metric.id === selectedId) ?? options[0];
  return <section className="mt-3" aria-labelledby="sport-evolution" data-id="element-1333" data-mp="3h9tjtp0jczvi">
      <SectionTitle data-id="element-1334" data-mp="3cagnmdnrygg9">
        <span id="sport-evolution" data-id="element-1335" data-mp="3cb07w10wdgxg">{t('metrics.sports.evolution.title')}</span>
      </SectionTitle>

      {!selected ? <p className="px-1 text-[12px] leading-snug text-muted" data-id="element-1336" data-mp="8s6z01j2dg9m">
          {t('metrics.sports.evolution.empty')}
        </p> : <>
          {options.length > 1 && <>
              <p className="mb-1 px-1 text-[11px] font-medium text-muted" data-id="element-1337" data-mp="3v81c3jmmnd0c">
                {t('metrics.sports.evolution.choose')}
              </p>
              <ul className="bee-rail -mx-2 mb-2 flex gap-1 overflow-x-auto px-2 pb-1" data-id="element-1338" data-mp="wjcbq7tpjsfp">
                {options.map(metric => <li key={metric.id} className="shrink-0" data-id="element-1339" data-mp="rp81nfp5b4ae">
                    <FilterChip label={tl(metric.label)} active={metric.id === selected.id} onClick={() => setSelectedId(metric.id)} data-id="element-1340" data-mp="2m06jk8l6l8dg" />
                  </li>)}
              </ul>
            </>}
          <ChartCard title={tl(selected.label)} question={t('metrics.sports.evolution.question')} series={[{
        id: selected.id,
        name: tl(selected.label),
        kind: selected.kind,
        points: sliceRange(buildSeries(selected.value, seedFor(selected.id), 0.05), range),
        color: chartColors.gold
      }]} range={range} onRangeChange={setRange} data-id="element-1341" data-mp="2h6ltd2ptxiot" />
        </>}
    </section>;
}